/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Modal,
  Progress,
  Row,
  Space,
  Statistic,
  Tabs,
  Tag,
  message,
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import VoucherReminderTemplateVariables from './VoucherReminderTemplateVariables';
import VoucherReminderAppliedVouchersModal from './VoucherReminderAppliedVouchersModal';
import voucherReminderApi from './voucherReminderApi';
import {
  formatUserById,
  resolveUserLabels,
} from './voucherReminderUsers';
import {
  canUsePageButton,
  findPageButton,
  formatTs,
  getPageButtonTitle,
  getRejectReason,
  msToScheduleTime,
  normalizeTranslationsInput,
  parseIdList,
  statusMeta,
  triggerLabel,
  VR_BUTTON_MODE,
  VR_STATUS,
} from './voucherReminderUtils';

export interface VoucherReminderDetailProps {
  id: number;
  currentUserId?: number | null;
  roleId?: number | null;
  pageInfo?: any;
  onClose?: () => void;
  onChanged?: () => void;
  onEdit?: (campaign: any) => void;
}

const VoucherReminderDetail = ({
  id,
  currentUserId,
  roleId,
  pageInfo,
  onClose,
  onChanged,
  onEdit,
}: VoucherReminderDetailProps) => {
  const intl = useIntl();
  const t = (key: string, values?: Record<string, any>) =>
    intl.formatMessage({ id: key }, values);
  const empty = t('voucherReminder.empty');

  const btnTitle = (mode: string, fallbackKey: string) =>
    getPageButtonTitle(
      findPageButton(pageInfo, mode),
      intl.locale,
      t(fallbackKey)
    );

  const allow = (mode: string) => canUsePageButton(pageInfo, mode, roleId);

  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [campaign, setCampaign] = useState<any>(null);
  const [report, setReport] = useState<{
    total: number;
    byStatus: Record<string, number>;
  } | null>(null);

  const [voucherModal, setVoucherModal] = useState(false);
  const [userLabels, setUserLabels] = useState<Record<number, string>>({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await voucherReminderApi.report(id);
      if (!res.ok || !res.data) {
        message.error(res.message || t('voucherReminder.msg.loadReportFail'));
        return;
      }
      const nextCampaign = res.data.campaign;
      setCampaign(nextCampaign);
      setReport(res.data.report);
      const labels = await resolveUserLabels(
        [
          nextCampaign?.createdBy,
          nextCampaign?.approvedBy,
          nextCampaign?.rejectedBy,
        ],
        pageInfo?.id
      );
      setUserLabels(labels);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const userLabel = (userId: any) => formatUserById(userId, userLabels, empty);

  const isCreator =
    currentUserId != null &&
    campaign?.createdBy != null &&
    Number(campaign.createdBy) === Number(currentUserId);

  const runAction = async (
    fn: () => Promise<{ ok: boolean; message?: string }>
  ) => {
    setActionLoading(true);
    try {
      const res = await fn();
      if (!res.ok) {
        message.error(res.message || t('voucherReminder.msg.actionFail'));
        return;
      }
      message.success(t('voucherReminder.msg.success'));
      await load();
      onChanged?.();
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = () => {
    let reason = '';
    Modal.confirm({
      title: t('voucherReminder.reject.title'),
      content: (
        <Input.TextArea
          rows={3}
          maxLength={500}
          placeholder={t('voucherReminder.reject.reason')}
          onChange={(e) => {
            reason = e.target.value;
          }}
        />
      ),
      okText: t('voucherReminder.reject.ok'),
      okButtonProps: { danger: true },
      onOk: () => runAction(() => voucherReminderApi.reject(id, reason || null)),
    });
  };

  const openVoucherList = () => setVoucherModal(true);

  const by = report?.byStatus || {};
  const total = report?.total || 0;
  const sent = by.sent || 0;
  const failed = by.failed || 0;
  const pending = by.pending || 0;
  const skipped = by.skipped || 0;
  const sentPct = total > 0 ? Math.round((sent / total) * 100) : 0;

  const st = statusMeta(campaign?.status, intl);
  const rejectReason = getRejectReason(campaign);
  const isRejected = campaign?.status === VR_STATUS.REJECTED;
  const channels = Array.isArray(campaign?.channels)
    ? campaign.channels.join(', ')
    : empty;
  const voucherIdList = parseIdList(campaign?.voucherIds);

  const actionButtons = (
    <Space wrap size="small">
      {campaign?.status === VR_STATUS.PENDING && allow(VR_BUTTON_MODE.APPROVE) && (
        <Button
          type="primary"
          size="small"
          loading={actionLoading}
          onClick={() => runAction(() => voucherReminderApi.approve(id))}
        >
          {btnTitle(VR_BUTTON_MODE.APPROVE, 'voucherReminder.action.approve')}
        </Button>
      )}
      {campaign?.status === VR_STATUS.PENDING && allow(VR_BUTTON_MODE.REJECT) && (
        <Button
          danger
          size="small"
          loading={actionLoading}
          onClick={handleReject}
        >
          {btnTitle(VR_BUTTON_MODE.REJECT, 'voucherReminder.action.reject')}
        </Button>
      )}
      {campaign?.status === VR_STATUS.ACTIVE && allow(VR_BUTTON_MODE.PAUSE) && (
        <Button
          size="small"
          loading={actionLoading}
          onClick={() =>
            runAction(() => voucherReminderApi.setStatus(id, 'paused'))
          }
        >
          {btnTitle(VR_BUTTON_MODE.PAUSE, 'voucherReminder.action.pause')}
        </Button>
      )}
      {(campaign?.status === VR_STATUS.ACTIVE ||
        campaign?.status === VR_STATUS.PAUSED ||
        campaign?.status === VR_STATUS.APPROVED) &&
        allow(VR_BUTTON_MODE.END) && (
          <Button
            danger
            size="small"
            loading={actionLoading}
            onClick={() =>
              runAction(() => voucherReminderApi.setStatus(id, 'ended'))
            }
          >
            {btnTitle(VR_BUTTON_MODE.END, 'voucherReminder.action.end')}
          </Button>
        )}
      {campaign?.status === VR_STATUS.PAUSED && allow(VR_BUTTON_MODE.RESUME) && (
        <Button
          type="primary"
          size="small"
          loading={actionLoading}
          onClick={() =>
            runAction(() => voucherReminderApi.setStatus(id, 'active'))
          }
        >
          {btnTitle(VR_BUTTON_MODE.RESUME, 'voucherReminder.action.resume')}
        </Button>
      )}
      {campaign?.status === VR_STATUS.REJECTED &&
        isCreator &&
        onEdit &&
        allow(VR_BUTTON_MODE.EDIT) && (
          <Button type="primary" size="small" onClick={() => onEdit(campaign)}>
            {btnTitle(VR_BUTTON_MODE.EDIT, 'voucherReminder.action.editResubmit')}
          </Button>
        )}
      <Button size="small" onClick={load} loading={loading}>
        {t('voucherReminder.action.refresh')}
      </Button>
      {onClose && (
        <Button size="small" onClick={onClose}>
          {t('voucherReminder.action.close')}
        </Button>
      )}
    </Space>
  );

  return (
    <div className="vr-detail">
      <style>{`
        .vr-detail .ant-card-head { min-height: 36px; padding: 0 12px; }
        .vr-detail .ant-card-head-title { padding: 8px 0; font-size: 13px; }
        .vr-detail .ant-card-body { padding: 12px; }
        .vr-detail .ant-descriptions-item-label,
        .vr-detail .ant-descriptions-item-content { padding: 6px 10px !important; }
        .vr-detail .ant-tabs-bar { margin-bottom: 8px; }
      `}</style>

      <Space
        style={{
          marginBottom: 12,
          width: '100%',
          justifyContent: 'space-between',
        }}
        align="start"
      >
        <Space wrap>
          <Tag color={st.color}>{st.label}</Tag>
          <span style={{ fontWeight: 600, fontSize: 15 }}>
            {campaign?.name || `Campaign #${id}`}
          </span>
        </Space>
        {actionButtons}
      </Space>

      {isRejected && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('voucherReminder.detail.rejectedTitle')}
          description={
            <div>
              <div style={{ whiteSpace: 'pre-wrap', marginBottom: 6 }}>
                <strong>{t('voucherReminder.config.rejectReason')}:</strong>{' '}
                {rejectReason || empty}
              </div>
              <div style={{ color: '#666', fontSize: 12 }}>
                {t('voucherReminder.config.rejectedByAt')}:{' '}
                {userLabel(campaign?.rejectedBy)} /{' '}
                {formatTs(campaign?.rejectedAt, intl.locale)}
              </div>
            </div>
          }
        />
      )}

      <Row gutter={16}>
        <Col span={14}>
          <Card
            size="small"
            title={t('voucherReminder.config.title')}
            loading={loading}
          >
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label={t('voucherReminder.col.id')}>
                {campaign?.id}
              </Descriptions.Item>
              <Descriptions.Item label={t('voucherReminder.col.status')}>
                <Tag color={st.color}>{st.label}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label={t('voucherReminder.config.trigger')}>
                {triggerLabel(campaign?.triggerType, intl)} /{' '}
                {campaign?.triggerValue}
              </Descriptions.Item>
              <Descriptions.Item label={t('voucherReminder.config.channels')}>
                {channels}
              </Descriptions.Item>
              <Descriptions.Item label={t('voucherReminder.config.schedule')}>
                {campaign?.scheduleType}
                {campaign?.scheduleType === 'daily_at'
                  ? ` @ ${msToScheduleTime(campaign?.scheduleTime) || empty}`
                  : ''}
              </Descriptions.Item>
              <Descriptions.Item label={t('voucherReminder.config.lastRun')}>
                {formatTs(campaign?.lastRunAt, intl.locale)}
              </Descriptions.Item>
              <Descriptions.Item label={t('voucherReminder.config.start')}>
                {formatTs(campaign?.startAt, intl.locale)}
              </Descriptions.Item>
              <Descriptions.Item label={t('voucherReminder.config.end')}>
                {formatTs(campaign?.endAt, intl.locale)}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('voucherReminder.config.voucherIds')}
                span={2}
              >
                <Space>
                  <span>
                    {t('voucherReminder.detail.voucherCount', {
                      count: voucherIdList.length,
                    })}
                  </span>
                  <Button
                    type="link"
                    size="small"
                    icon={<EyeOutlined />}
                    disabled={!voucherIdList.length}
                    onClick={openVoucherList}
                    style={{ padding: 0 }}
                  >
                    {t('voucherReminder.detail.viewVouchers')}
                  </Button>
                </Space>
              </Descriptions.Item>
              <Descriptions.Item
                label={t('voucherReminder.config.deeplink')}
                span={2}
              >
                {campaign?.deeplink || empty}
              </Descriptions.Item>
              <Descriptions.Item label={t('voucherReminder.config.createdBy')}>
                {userLabel(campaign?.createdBy)}
              </Descriptions.Item>
              <Descriptions.Item
                label={t('voucherReminder.config.approvedByAt')}
              >
                {userLabel(campaign?.approvedBy)} /{' '}
                {formatTs(campaign?.approvedAt, intl.locale)}
              </Descriptions.Item>
              {isRejected && (
                <>
                  <Descriptions.Item
                    label={t('voucherReminder.config.rejectedByAt')}
                  >
                    {userLabel(campaign?.rejectedBy)} /{' '}
                    {formatTs(campaign?.rejectedAt, intl.locale)}
                  </Descriptions.Item>
                  <Descriptions.Item
                    label={t('voucherReminder.config.rejectReason')}
                    span={2}
                  >
                    <span style={{ whiteSpace: 'pre-wrap' }}>
                      {rejectReason || empty}
                    </span>
                  </Descriptions.Item>
                </>
              )}
            </Descriptions>
          </Card>

          <Card
            size="small"
            title={t('voucherReminder.report.title')}
            loading={loading}
            style={{ marginTop: 12 }}
          >
            <Row gutter={12}>
              <Col span={6}>
                <Statistic title={t('voucherReminder.report.total')} value={total} />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('voucherReminder.report.sent')}
                  value={sent}
                  valueStyle={{ color: '#3f8600' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('voucherReminder.report.failed')}
                  value={failed}
                  valueStyle={{ color: '#cf1322' }}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title={t('voucherReminder.report.pendingSkipped')}
                  value={`${pending} / ${skipped}`}
                />
              </Col>
            </Row>
            <Progress
              percent={sentPct}
              status={failed > 0 ? 'exception' : 'active'}
              style={{ marginTop: 12 }}
              format={() => t('voucherReminder.report.sentPct', { pct: sentPct })}
            />
          </Card>
        </Col>

        <Col span={10}>
          <Card
            size="small"
            title={t('voucherReminder.form.content')}
            extra={<VoucherReminderTemplateVariables />}
            loading={loading}
            style={{ height: '100%' }}
          >
            <Tabs size="small" type="card">
              {normalizeTranslationsInput(campaign?.translations).map((tr) => (
                <Tabs.TabPane
                  tab={t(`voucherReminder.lang.${tr.language}`)}
                  key={tr.language}
                >
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ color: '#888', marginBottom: 4 }}>
                      {t('voucherReminder.form.title')}
                    </div>
                    <div>{tr.title || campaign?.title || empty}</div>
                  </div>
                  <div>
                    <div style={{ color: '#888', marginBottom: 4 }}>
                      {t('voucherReminder.form.body')}
                    </div>
                    <div style={{ whiteSpace: 'pre-wrap' }}>
                      {tr.body ||
                        (tr.language === 'vi' ? campaign?.body : '') ||
                        empty}
                    </div>
                  </div>
                </Tabs.TabPane>
              ))}
            </Tabs>
          </Card>
        </Col>
      </Row>

      <VoucherReminderAppliedVouchersModal
        visible={voucherModal}
        onClose={() => setVoucherModal(false)}
        voucherIds={campaign?.voucherIds}
        pageInfo={pageInfo}
      />
    </div>
  );
};

export default VoucherReminderDetail;
